import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { humanizeColumnName } from '../../lib/formatters';
import DataVisuals from '../DataVisuals';
import { DemographicHeatmap } from './DemographicHeatmap';
import { HelpCircle } from 'lucide-react';

export function DataVisualsTab({
  selectedSimId,
  allBreakdowns,
  aggregation,
  orderedResponseScale,
  scaleColors,
  scaleColorMap,
  onSwitchTab,
}) {
  const scale = orderedResponseScale.length > 0
    ? orderedResponseScale
    : Object.keys(aggregation?.percentages || {});

  return (
    <div className="space-y-6">
      <DataVisuals simulationId={selectedSimId} onSwitchTab={onSwitchTab} />

      {/* Response-by-Demographic Heatmap */}
      <DemographicHeatmap
        allBreakdowns={allBreakdowns}
        orderedResponseScale={scale}
        scaleColors={scaleColors}
        scaleColorMap={scaleColorMap}
        globalPcts={aggregation?.percentages || {}}
      />

      {/* Key Demographic Differences */}
      {allBreakdowns && Object.keys(allBreakdowns).length > 0 && (
        <Card className="border border-border" data-testid="demographic-insights">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Key Demographic Differences</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Groups showing the largest deviation from the overall average response
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <DemographicDifferences
              allBreakdowns={allBreakdowns}
              globalPcts={aggregation?.percentages || {}}
              scale={scale}
              scaleColors={scaleColors}
              scaleColorMap={scaleColorMap}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DemographicDifferences({ allBreakdowns, globalPcts, scale, scaleColors, scaleColorMap }) {
  if (scale.length < 2) return <p className="text-sm text-muted-foreground">Not enough response options for analysis</p>;

  const divergences = [];
  for (const [demoName, demoData] of Object.entries(allBreakdowns)) {
    if (!demoData?.breakdown || Object.keys(demoData.breakdown).length < 2) continue;
    for (const [group, data] of Object.entries(demoData.breakdown)) {
      if (!data?.percentages) continue;
      const count = Object.values(data.distribution || {}).reduce((s, v) => s + v, 0);
      if (count < 3) continue;
      let totalDivergence = 0;
      for (const k of scale) totalDivergence += Math.abs((data.percentages[k] || 0) - (globalPcts[k] || 0));
      const divergence = totalDivergence / 2;
      const sorted = Object.entries(data.percentages).sort((a, b) => b[1] - a[1]);
      divergences.push({
        group, demo: demoName, divergence, count,
        topResponse: sorted[0]?.[0], topPct: sorted[0]?.[1], pcts: data.percentages,
      });
    }
  }

  divergences.sort((a, b) => b.divergence - a.divergence);
  const top = divergences.slice(0, 8);

  if (top.length === 0) return <p className="text-sm text-muted-foreground">No significant demographic differences detected</p>;

  const maxDivergence = top[0]?.divergence || 1;

  return (
    <TooltipProvider>
      <div className="space-y-3">
        {top.map((item) => (
          <div key={`${item.demo}-${item.group}`} className="flex items-center gap-4 p-3 rounded-lg bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-700">
            {/* Divergence score with color coding */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg cursor-help shrink-0",
                  item.divergence > 15 ? "bg-negative-soft  text-negative-soft-foreground " : item.divergence > 8 ? "bg-warning-soft  text-warning-soft-foreground " : "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400"
                )} data-testid={`divergence-score-${item.demo}-${item.group}`}>
                  {item.divergence.toFixed(0)}
                </div>
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-[220px]">
                <p className="font-semibold mb-1">Divergence Score: {item.divergence.toFixed(1)}</p>
                <p>How different this group's responses are from the overall average. Higher = more distinct.</p>
                <div className="mt-1.5 space-y-0.5 text-slate-400">
                  <p>&gt;15 = Strong divergence</p>
                  <p>8-15 = Moderate</p>
                  <p>&lt;8 = Mild</p>
                </div>
              </TooltipContent>
            </Tooltip>

            {/* Group info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-800 dark:text-slate-100">{item.group}</span>
                <Badge variant="outline" className="text-[10px]">{humanizeColumnName(item.demo)}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {item.topPct?.toFixed(0)}% responded "{item.topResponse}" &bull; n={item.count}
              </p>
            </div>

            {/* Mini distribution bar with tooltip */}
            <div className="w-36 hidden sm:block">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="cursor-help">
                    <div className="flex h-3.5 rounded overflow-hidden border border-slate-200 dark:border-slate-600">
                      {scale.map((resp) => {
                        const pct = item.pcts[resp] || 0;
                        return pct > 0 ? (
                          <div key={resp} style={{ width: `${pct}%`, backgroundColor: scaleColorMap?.[resp] || scaleColors[scale.indexOf(resp)] || '#94A3B8' }} />
                        ) : null;
                      })}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-xs">
                  <p className="font-semibold mb-1">Response distribution for {item.group}</p>
                  {scale.map((resp) => {
                    const pct = item.pcts[resp] || 0;
                    const globalPct = globalPcts[resp] || 0;
                    const diff = pct - globalPct;
                    return (
                      <div key={resp} className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: scaleColorMap?.[resp] || '#94A3B8' }} />
                        <span>{resp}: {pct.toFixed(0)}%</span>
                        <span className={cn("text-[10px]", diff > 0 ? "text-positive" : diff < 0 ? "text-negative" : "text-slate-400")}>
                          ({diff > 0 ? '+' : ''}{diff.toFixed(0)}pp)
                        </span>
                      </div>
                    );
                  })}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        ))}

        {/* Legend / How to read */}
        <div className="pt-3 border-t border-border">
          <div className="flex items-start gap-4 flex-wrap">
            <p className="text-xs text-muted-foreground flex-1">
              <span className="font-medium text-slate-600 dark:text-slate-300">How to read:</span> The number shows how different each group's responses are from the overall average. Higher = more distinct.
            </p>
            <div className="flex items-center gap-3 text-[10px] text-slate-500 shrink-0">
              <span className="font-medium">Colored bar:</span>
              <div className="flex items-center gap-1">
                {scale.slice(0, Math.min(scale.length, 3)).map((resp, i) => (
                  <div key={resp} className="flex items-center gap-0.5">
                    <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: scaleColorMap?.[resp] || scaleColors[i] || '#94A3B8' }} />
                    <span className="truncate max-w-[60px]">{resp}</span>
                  </div>
                ))}
                {scale.length > 3 && <span>...</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
